const runtimeKeys = [
  'REACT_APP_API_BASE_URL',
  'REACT_APP_AWS_COGNITO_REGION',
  'REACT_APP_AWS_USER_POOLS_ID',
  'REACT_APP_AWS_USER_POOLS_WEB_CLIENT_ID',
  'REACT_APP_MUI_PRO_X_LICENSE_KEY',
  // Google SSO (optional). When REACT_APP_AWS_COGNITO_DOMAIN is set, the app
  // enables Cognito Hosted UI federated sign-in. Leave unset to keep the
  // existing email/password-only behaviour.
  'REACT_APP_AWS_COGNITO_DOMAIN',
  'REACT_APP_OAUTH_REDIRECT_SIGN_IN',
  'REACT_APP_OAUTH_REDIRECT_SIGN_OUT',
  // Part Type Studio API (separate service; the backend PR provides the URL). See api/partTypeStudio/config.ts.
  'REACT_APP_PART_TYPE_STUDIO_API_URL',
] as const;

type RuntimeKey = (typeof runtimeKeys)[number];
type RuntimeValues = Record<RuntimeKey, string>;
type RuntimeWindow = Window & {
  __RUNTIME_CONFIG__?: Partial<Record<RuntimeKey, string>>;
};

const DEV_DEFAULTS: RuntimeValues = {
  REACT_APP_API_BASE_URL: 'https://api.dev.dismantly.ai/',
  REACT_APP_AWS_COGNITO_REGION: 'us-east-1',
  REACT_APP_AWS_USER_POOLS_ID: 'us-east-1_VMY3cfN7s',
  REACT_APP_AWS_USER_POOLS_WEB_CLIENT_ID: 't98erdie1vccreaipaf1ftnge',
  REACT_APP_MUI_PRO_X_LICENSE_KEY:
    'dfabb5139f89c0390f82c9e9a7c50114Tz0xMjYyNTUsRT0xODAzNzcyNzk5MDAwLFM9cHJlbWl1bSxMTT1zdWJzY3JpcHRpb24sUFY9aW5pdGlhbCxLVj0y',
  REACT_APP_AWS_COGNITO_DOMAIN: '',
  REACT_APP_OAUTH_REDIRECT_SIGN_IN: '',
  REACT_APP_OAUTH_REDIRECT_SIGN_OUT: '',
  REACT_APP_PART_TYPE_STUDIO_API_URL: '',
};

const PROD_DEFAULTS: RuntimeValues = {
  REACT_APP_API_BASE_URL: 'https://api.dismantly.ai/',
  REACT_APP_AWS_COGNITO_REGION: 'ap-southeast-2',
  REACT_APP_AWS_USER_POOLS_ID: 'ap-southeast-2_XwNqBcweZ',
  REACT_APP_AWS_USER_POOLS_WEB_CLIENT_ID: '6aab52bq8lt9dqrpimnj9gec1t',
  REACT_APP_MUI_PRO_X_LICENSE_KEY:
    'dfabb5139f89c0390f82c9e9a7c50114Tz0xMjYyNTUsRT0xODAzNzcyNzk5MDAwLFM9cHJlbWl1bSxMTT1zdWJzY3JpcHRpb24sUFY9aW5pdGlhbCxLVj0y',
  REACT_APP_AWS_COGNITO_DOMAIN: '',
  REACT_APP_OAUTH_REDIRECT_SIGN_IN: '',
  REACT_APP_OAUTH_REDIRECT_SIGN_OUT: '',
  REACT_APP_PART_TYPE_STUDIO_API_URL: '',
};

const runtimeWindow = (typeof window !== 'undefined' ? window : undefined) as RuntimeWindow | undefined;
const isLocalHost =
  runtimeWindow?.location?.hostname === 'localhost' || runtimeWindow?.location?.hostname === '127.0.0.1';
const defaults = isLocalHost ? DEV_DEFAULTS : PROD_DEFAULTS;

const getRuntimeValue = (key: RuntimeKey): string => {
  const runtimeValue = runtimeWindow?.__RUNTIME_CONFIG__?.[key];

  if (typeof runtimeValue === 'string' && runtimeValue.trim()) {
    return runtimeValue;
  }

  const envValue = process.env[key];
  if (typeof envValue === 'string' && envValue.trim()) {
    return envValue;
  }

  return defaults[key];
};

export const runtimeConfig: RuntimeValues = runtimeKeys.reduce(
  (accumulator, key) => ({
    ...accumulator,
    [key]: getRuntimeValue(key),
  }),
  {} as RuntimeValues,
);
